import {
  ImageType,
  ImageTypeAuto,
  type ImageData,
  type ImageUrlForType,
} from '@responsive-image/core';

import { envConfig } from '#constants/envConfig';
import { buildImgproxyUrl, type ImgproxyOptions } from './imgproxyUrl';

export type LoaderConfig = {
  domain: string;
};

export type CoreOptions = {
  formats?: ImageType[] | ImageTypeAuto;
  quality?: number;
  aspectRatio?: number;
};

type LoaderOptions = CoreOptions & {
  params?: Record<string, string | number>;
};

const typeToImgproxyFormat: Record<string, ImgproxyOptions['format']> = {
  png: 'png',
  webp: 'webp',
  jpg: 'jpg',
  jpeg: 'jpg',
  avif: 'avif',
};

// Per responsive-image/core typing, type defaults to 'png' for the loader's
// imageUrlFor, and we map that onto an imgproxy format.
export const isParsableUrl = (value: string): boolean => {
  try {
    return Boolean(new URL(value));
  } catch {
    return false;
  }
};

export const getResizedImageUrl = (
  image: string,
  width: number,
  options: LoaderOptions = {},
  type: ImageUrlForType = 'png'
): string => {
  // Relative URLs (e.g. /assets/foo.png) are not absolute sources — leave as-is.
  if (!image || image.startsWith('/')) return image;

  // Validate absolute URL early so failures don't cascade through the resizer.
  // Parsing by hand instead of URL.canParse: that one lands only in Safari 17 /
  // Chrome 120, and on anything older this guard threw instead of guarding.
  if (!isParsableUrl(image)) return image;

  // Already resized: the backend signs its own imgproxy links for avatars it sends over WS,
  // and wrapping one in another makes imgproxy fetch its own output through the public host.
  if (envConfig.IMGPROXY_URL && image.startsWith(envConfig.IMGPROXY_URL)) return image;

  if (
    envConfig.IMGPROXY_ENABLED &&
    envConfig.IMGPROXY_URL &&
    envConfig.IMGPROXY_KEY &&
    envConfig.IMGPROXY_SALT
  ) {
    try {
      return buildImgproxyUrl(
        {
          baseUrl: envConfig.IMGPROXY_URL,
          keyHex: envConfig.IMGPROXY_KEY,
          saltHex: envConfig.IMGPROXY_SALT,
        },
        image,
        {
          width,
          quality: options.quality ?? 70,
          format: typeToImgproxyFormat[type] ?? 'webp',
          fit: 'fill',
        }
      );
    } catch {
      // Signing failed for some reason — serve the untouched source rather than 404.
      return image;
    }
  }

  // weserv was retired on 2026-08-12, so there is no second resizer to fall back to:
  // hand back the source and let the browser scale it. Losing imgproxy config now
  // costs bytes, not broken images.
  return image;
};

/**
 * Картинка из нашего хранилища – через imgproxy под нужную ширину, чужая – как есть.
 *
 * imgproxy пускает только свои источники (`IMGPROXY_ALLOWED_SOURCES`), и обложка со Steam или
 * Xbox, завёрнутая в него, вернулась бы 404. Туда, где рядом лежат наши и чужие адреса (плитки
 * игр в профиле), – этот помощник, а не `getResizedImageUrl` напрямую.
 */
export const getMediaImageUrl = (image: string, width: number): string =>
  envConfig.MEDIA_URL && image.startsWith(envConfig.MEDIA_URL)
    ? getResizedImageUrl(image, width, {}, 'webp')
    : image;

export function imageLoader(image: string, options: LoaderOptions = {}): ImageData {
  const imageData: ImageData = {
    imageTypes: options.formats ?? 'auto',
    imageUrlFor(width: number, _type: ImageUrlForType = 'png'): string {
      return getResizedImageUrl(image, width, options, 'webp');
    },
  };

  if (options.aspectRatio) {
    imageData.aspectRatio = options.aspectRatio;
  }

  return imageData;
}
